import React, { useContext, useEffect, useState, useMemo } from "react";
import { Button, Col, Form, FormControl, InputGroup, Modal, Row, Table, Accordion, Card } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { GlobalContext } from "../Context/globalContext";
import FirebaseApp from '../firebase';
import Swal from "sweetalert2";
import { ref, get, set, onValue } from 'firebase/database';

export const Companies = () => {
    const globalContext = useContext(GlobalContext);
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [salesManagers, setSalesManagers] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState({ created_by: globalContext.userEmail });
    const [totalLicenses, setTotalLicenses] = useState(0);
    const [show, setShow] = useState(false);
    const [search, setSearch] = useState('');
    const [addModal, setAddModal] = useState(false);
    const [addManager, setAddManager] = useState(false);
    const [selectedSalesManager, setSelectedSalesManager] = useState("");
    const [salesManagerSearch, setSalesManagerSearch] = useState('');
    const [managerCompanySearches, setManagerCompanySearches] = useState({});

    const isSuperAdmin = useMemo(() => {
        return globalContext.userRole === 'SuperAdmin';
    }, [globalContext.userRole]);

    const addNewCompany = () => {
        setAddModal(true);
        setSelectedCompany({ created_by: globalContext.userEmail });
    }

    const addNewSalesManager = () => {
        setAddManager(true);
        setSelectedSalesManager("");
    }

    useEffect(() => {
        if (isSuperAdmin) {
            fetchSalesManagers();
        } else {
            fetchAllRelatedCompanies();
        }
    }, [isSuperAdmin]);

    const fetchSalesManagers = () => {
        globalContext.setLoader(true);
        FirebaseApp().then(db => {
            const usersRef = ref(db, "SalesManager");

            return onValue(usersRef, (snapshot) => {
                const managers = [];
                snapshot.forEach(childSnapshot => {
                    managers.push({
                        id: childSnapshot.key,
                        email: childSnapshot.val(),
                        companies: []
                    });
                });

                managers.sort((a, b) => {
                    if (a.email === globalContext.userEmail) return -1;
                    if (b.email === globalContext.userEmail) return 1;
                    return 0;
                });

                setSalesManagers(managers);

                // Fetch companies for each manager
                managers.forEach(manager => {
                    fetchCompaniesForManager(manager.email);
                });

                globalContext.setLoader(false);
            }, (err) => {
                console.log('error --->', err);
                globalContext.setLoader(false);
            });
        }).catch(err => {
            console.log('error --->', err);
            globalContext.setLoader(false);
        });
    };

    const fetchCompaniesForManager = (managerEmail) => {
        FirebaseApp().then(db => {
            const companiesRef = ref(db, "CompanyKeys");

            return onValue(companiesRef, (snapshot) => {
                const companies = [];
                snapshot.forEach(childSnapshot => {
                    if (childSnapshot.val().created_by && childSnapshot.val().created_by === managerEmail) {
                        companies.push({ "company_key": childSnapshot.key, ...childSnapshot.val() });
                    }
                });

                setSalesManagers(prev =>
                    prev.map(manager =>
                        manager.email === managerEmail
                            ? { ...manager, companies }
                            : manager
                    )
                );
            });
        }).catch(err => {
            console.log('error fetching companies for manager --->', err);
        });
    };

    const fetchAllRelatedCompanies = () => {
        globalContext.setLoader(true);
        FirebaseApp().then(db => {
            const dbRef = ref(db, "CompanyKeys");
            return onValue(dbRef, (snapshot) => {
                const records = []
                snapshot.forEach(childSnapshot => {
                    if (childSnapshot.val().created_by && childSnapshot.val().created_by === globalContext.userEmail) {
                        records.push({ "company_key": childSnapshot.key, ...childSnapshot.val() });
                    }
                })
                setData(records);
                globalContext.setLoader(false);
            }, (err) => {
                console.log('error --->', err);
                globalContext.setLoader(false);
            });
        }).catch(err => {
            console.log('error --->', err);
            globalContext.setLoader(false);
        })
    }

    const filteredData = useMemo(() => {
        return data.filter(d =>
            d.company_name.toLowerCase().includes(search.toLowerCase()) ||
            d.company_key.toLowerCase().includes(search.toLowerCase())
        );
    }, [search, data]);

    const filteredSalesManagers = useMemo(() => {
        return salesManagers.filter(manager =>
            manager.email.toLowerCase().includes(salesManagerSearch.toLowerCase())
        );
    }, [salesManagerSearch, salesManagers]);

    const getFilteredCompaniesForManager = (manager) => {
        const searchTerm = managerCompanySearches[manager.id] || '';
        return manager.companies.filter(company =>
            company.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            company.company_key.toLowerCase().includes(searchTerm.toLowerCase())
        );
    };

    const handleManagerCompanySearch = (managerId, searchTerm) => {
        setManagerCompanySearches(prev => ({
            ...prev,
            [managerId]: searchTerm
        }));
    };

    const closeModal = () => {
        setShow(false);
        setSelectedCompany({ created_by: globalContext.userEmail });
        setTotalLicenses(0);
    }

    const closeAddModal = () => {
        setAddModal(false);
        setSelectedCompany({ created_by: globalContext.userEmail });
    }

    const closeAddManager = () => {
        setAddManager(false);
        setSelectedSalesManager("");
    }

    const editLicense = (company) => {
        setSelectedCompany(company);
        setTotalLicenses(company.total_licenses);
        setShow(true);
    }

    const toggleStatus = (companyKey, status) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You want to update the status of this company key!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, update it!'
        }).then((result) => {
            if (result.isConfirmed) {
                FirebaseApp().then(db => {
                    const dbRef = ref(db, `CompanyKeys/${companyKey}`);
                    return get(dbRef).then((snapshot) => {
                        if (snapshot.exists()) {
                            const newStatus = !status;

                            return set(dbRef, { ...snapshot.val(), key_status: newStatus })
                                .then(() => {
                                    toast.success('Company Key Status Updated Successfully');
                                })
                                .catch(err => {
                                    console.error('Error updating Firebase:', err);
                                    toast.error('Error while updating company key status');
                                });

                        }
                    }).catch(err => {
                        console.log('error --->', err);
                        toast.error('Error While Updating Company Key Status');
                    });
                }).catch(err => {
                    console.log('error --->', err);
                    toast.error('Error While Updating Company Key Status');
                })
            }
        })
    };

    const updateCompanyLicenses = () => {
        const activeUsers = parseInt(selectedCompany.total_licenses) - parseInt(selectedCompany.available_licenses);
        if (totalLicenses < activeUsers) {
            toast.error("Total licenses should be greater or equal to active licenses");
            return;
        }

        FirebaseApp().then(db => {
            const dbRef = ref(db, `CompanyKeys/${selectedCompany.company_key}`);
            return set(dbRef, { ...selectedCompany, total_licenses: parseInt(totalLicenses), available_licenses: parseInt(totalLicenses) - activeUsers })
                .then(() => {
                    toast.success('Company Key Updated Successfully');
                    closeModal();
                })
                .catch(err => {
                    console.error('Error updating Firebase:', err);
                    toast.error('Error while updating company key');
                });
        }).catch(err => {
            console.log('error --->', err);
            toast.error('Error While Updating Company Key');
        })

    }

    const handleKeyChange = (val, key) => {
        setSelectedCompany({ ...selectedCompany, [key]: val });
    }

    const createSalesManager = () => {
        if (!selectedSalesManager) {
            toast.error("Please enter a sales manager email!");
            return;
        }

        FirebaseApp().then(db => {
            const usersRef = ref(db, "SalesManager");

            return get(usersRef).then((snapshot) => {
                const existingManagers = snapshot.val() || {};
                const managerEmails = Object.values(existingManagers);

                if (managerEmails.includes(selectedSalesManager)) {
                    toast.error("Sales Manager already exists!");
                    return;
                }

                const existingKeys = Object.keys(existingManagers).map(key => parseInt(key, 10)).filter(key => !isNaN(key));
                const largestKey = existingKeys.length ? Math.max(...existingKeys) : 0;
                const newManagerKey = largestKey + 1;
                const newManagerRef = ref(db, `SalesManager/${newManagerKey}`);

                return set(newManagerRef, selectedSalesManager)
                    .then(() => {
                        toast.success("Sales Manager added successfully!");
                        closeAddManager();
                    })
                    .catch(err => {
                        console.error("Error adding Sales Manager:", err);
                        toast.error("Error while adding Sales Manager!");
                    });
            }).catch(err => {
                console.error("Firebase error:", err);
                toast.error("Error connecting to Firebase!");
            });
        }).catch(err => {
            console.error("Firebase error:", err);
            toast.error("Error connecting to Firebase!");
        });
    };


    const createCompany = () => {
        if (!selectedCompany.company_key || !selectedCompany.company_name || !selectedCompany.company_password || !selectedCompany.total_licenses || selectedCompany.total_licenses <= 0 || !selectedCompany.created_by) {
            toast.error("All fields are required and licenses should be greater than 0!");
            return;
        }

        FirebaseApp().then(db => {
            const dbRef = ref(db, `CompanyKeys/${selectedCompany.company_key}`);

            return get(dbRef).then((snapshot) => {
                if (snapshot.exists()) {
                    toast.error("Company key already exists!");
                    return;
                }

                return set(dbRef, {
                    ...selectedCompany,
                    total_licenses: parseInt(selectedCompany.total_licenses),
                    available_licenses: parseInt(selectedCompany.total_licenses),
                    key_status: true,
                }).then(() => {
                    toast.success("Company added successfully!");
                    closeAddModal();
                }).catch(err => {
                    console.error("Error adding company:", err);
                    toast.error("Error while adding company!");
                });
            }).catch(err => {
                console.error("Firebase error:", err);
                toast.error("Error connecting to Firebase!");
            });
        }).catch(err => {
            console.error("Firebase error:", err);
            toast.error("Error connecting to Firebase!");
        });
    }

    // Render Companies Table
    const renderCompaniesTable = (companiesToRender) => (
        <Table striped bordered hover>
            <thead>
                <tr>
                    <th>Company Key</th>
                    <th>Company Name</th>
                    <th>Company Password</th>
                    <th>Available Licenses</th>
                    <th>Total Licenses</th>
                    <th>Status</th>
                    <th>Action</th>
                </tr>
            </thead>
            <tbody>
                {companiesToRender.length > 0 ?
                    companiesToRender.map((d) =>
                        <tr key={d.company_key}>
                            <td>{d.company_key}</td>
                            <td>{d.company_name}</td>
                            <td>{d.company_password}</td>
                            <td>{d.available_licenses}</td>
                            <td>{d.total_licenses}</td>
                            <td>
                                <Form.Check
                                    type="switch"
                                    id={`switch-${d.company_key}`}
                                    checked={d.key_status}
                                    onChange={() => toggleStatus(d.company_key, d.key_status)}
                                />
                            </td>
                            <td>
                                <Button className="btn btn-primary me-2" onClick={() => navigate(`/company/${d.company_key}`)}>View Dashboard</Button>
                                <Button className="btn btn-primary me-2" onClick={() => navigate(`/devices/${d.company_key}`)}>View Devices</Button>
                                <Button className="btn btn-secondary" onClick={() => editLicense(d)}>Edit Licenses</Button>
                            </td>
                        </tr>
                    ) :
                    <tr>
                        <td colSpan={7}>
                            <h5 className="text-center">No data</h5>
                        </td>
                    </tr>
                }
            </tbody>
        </Table>
    );

    return (
        <main id="main" className="main">
            <div className="pagetitle">
                <h1>Companies</h1>
                {isSuperAdmin ? (
                    <Row>
                        <Col md={4}>
                            <div className="text-left mt-3">
                                <InputGroup>
                                    <FormControl
                                        className="search-field"
                                        placeholder="Search by Sales Manager Email"
                                        aria-label="Search"
                                        value={salesManagerSearch}
                                        onChange={(e) => setSalesManagerSearch(e.target.value)}
                                    />
                                </InputGroup>
                            </div>
                        </Col>
                        <Col>
                            <div className="text-right mt-3">
                                <Button onClick={() => addNewSalesManager()} className="me-2">Add Sales Manager</Button>
                                <Button onClick={() => addNewCompany()}>Add Company</Button>
                            </div>
                        </Col>
                    </Row>
                ) : (
                    <Row>
                        <Col md={4}>
                            <div className="text-left mt-3">
                                <InputGroup>
                                    <FormControl
                                        className="search-field"
                                        placeholder="Search by Company Key or Company Name"
                                        aria-label="Search"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </InputGroup>
                            </div>
                        </Col>
                        <Col>
                            <div className="text-right mt-3">
                                <Button onClick={() => addNewCompany()}>Add Company</Button>
                            </div>
                        </Col>
                    </Row>
                )}
            </div>
            <section className="section dashboard">
                {isSuperAdmin ? (
                    <Accordion>
                        {filteredSalesManagers.length > 0 ? (
                            filteredSalesManagers.map((manager, index) => (
                                <Card key={manager.id}>
                                    <Accordion.Item eventKey={index.toString()}>
                                        <Accordion.Header>
                                            {manager.name} ({manager.email}) - {manager.companies.length} Companies
                                        </Accordion.Header>
                                        <Accordion.Body>
                                            <div className="mt-3 mb-3">
                                                <InputGroup>
                                                    <FormControl
                                                        className="search-field"
                                                        placeholder="Search by Company Key or Company Name"
                                                        aria-label="Search"
                                                        value={managerCompanySearches[manager.id] || ''}
                                                        onChange={(e) => handleManagerCompanySearch(manager.id, e.target.value)}
                                                    />
                                                </InputGroup>
                                            </div>
                                            {renderCompaniesTable(getFilteredCompaniesForManager(manager))}
                                        </Accordion.Body>
                                    </Accordion.Item>
                                </Card>
                            ))
                        ) : (
                            <Card className="text-center p-4">
                                <h5>No Sales Managers found</h5>
                            </Card>
                        )}
                    </Accordion>
                ) : (
                    renderCompaniesTable(filteredData)
                )}

                {/* Modal for editing licenses */}
                <Modal show={show} onHide={() => closeModal()}>
                    <Modal.Header closeButton>
                        <Modal.Title>Company Details</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Form.Group className="mb-3 mt-2" controlId="exampleForm.ControlInput">
                                <Form.Label>Total Licenses</Form.Label>
                                <Form.Control type="number" value={totalLicenses} onChange={(e) => setTotalLicenses(e.target.value)} />
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => updateCompanyLicenses()}>
                            Update
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Modal for adding new sales manager */}
                <Modal show={addManager} onHide={() => closeAddManager()}>
                    <Modal.Header closeButton>
                        <Modal.Title>Add New Sales Manager</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Form.Group className="mb-3 mt-2" controlId="exampleForm.ControlInput">
                                <Form.Label>Sales Manager Email</Form.Label>
                                <Form.Control type="email" value={selectedSalesManager} onChange={(e) => setSelectedSalesManager(e.target.value)} />
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="primary" onClick={createSalesManager}>
                            Add
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Modal for adding new company */}
                <Modal show={addModal} onHide={closeAddModal} size="lg" centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Add New Company</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>Company Key</Form.Label>
                                <Form.Control type="text" value={selectedCompany.company_key || ''} onChange={(e) => handleKeyChange(e.target.value, "company_key")} />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Company Name</Form.Label>
                                <Form.Control type="text" value={selectedCompany.company_name || ''} onChange={(e) => handleKeyChange(e.target.value, "company_name")} />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Company Password</Form.Label>
                                <Form.Control type="text" value={selectedCompany.company_password || ''} onChange={(e) => handleKeyChange(e.target.value, "company_password")} />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Total Licenses</Form.Label>
                                <Form.Control type="number" value={selectedCompany.total_licenses || ''} onChange={(e) => handleKeyChange(e.target.value, "total_licenses")} />
                            </Form.Group>

                            {isSuperAdmin && (
                                <Form.Group className="mb-3">
                                    <Form.Label>Assign To</Form.Label>
                                    <Form.Control type="email" value={selectedCompany.created_by || ''} onChange={(e) => handleKeyChange(e.target.value, "created_by")} />
                                </Form.Group>
                            )}
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="primary" onClick={createCompany}>Add</Button>
                    </Modal.Footer>
                </Modal>
            </section>
        </main>
    )
}
